import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";

const I18nContext = createContext(null);

const SUPPORTED_LANGUAGES = {
  en: "English",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  pt: "Português",
  zh: "中文",
  ja: "日本語"
};

export function I18nProvider({ children }) {
  const { api, isAuthenticated } = useAuth();
  const [language, setLanguage] = useState(localStorage.getItem("language") || "en");
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(true);

  // Load translations
  const loadTranslations = useCallback(async (lang) => {
    try {
      const response = await api.get(`/settings/translations/${lang}`);
      setTranslations(response.data.translations);
    } catch (error) {
      console.error("Failed to load translations:", error);
      // Fallback to English if translation fails
      if (lang !== "en") {
        try {
          const response = await api.get("/settings/translations/en");
          setTranslations(response.data.translations);
        } catch {
          setTranslations({});
        }
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Load user's language preference on login
  useEffect(() => {
    if (isAuthenticated) {
      api.get("/settings/user/language")
        .then((response) => {
          const userLang = response.data.language || "en";
          setLanguage(userLang);
          localStorage.setItem("language", userLang);
          loadTranslations(userLang);
        })
        .catch(() => {
          loadTranslations(language);
        });
    } else {
      loadTranslations(language);
    }
  }, [isAuthenticated, api, loadTranslations]);

  // Change language
  const changeLanguage = async (newLang) => {
    if (!SUPPORTED_LANGUAGES[newLang]) return;
    
    setLanguage(newLang);
    localStorage.setItem("language", newLang);
    await loadTranslations(newLang);
    
    // Save to backend if authenticated
    if (isAuthenticated) {
      try {
        await api.put("/settings/user/language", { language: newLang });
      } catch (error) {
        console.error("Failed to save language preference:", error);
      }
    }
  };

  // Translation function
  const t = useCallback((key, fallback) => {
    return translations[key] || fallback || key;
  }, [translations]);

  // Format number based on locale
  const formatNumber = useCallback((num) => {
    return new Intl.NumberFormat(language).format(num);
  }, [language]);

  // Format currency based on locale
  const formatCurrency = useCallback((amount, currency = "USD") => {
    return new Intl.NumberFormat(language, {
      style: "currency",
      currency: currency
    }).format(amount);
  }, [language]);

  // Format date based on locale
  const formatDate = useCallback((date, options = {}) => {
    return new Intl.DateTimeFormat(language, options).format(new Date(date));
  }, [language]);

  const value = {
    language,
    languages: SUPPORTED_LANGUAGES,
    changeLanguage,
    t,
    formatNumber,
    formatCurrency,
    formatDate,
    loading
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
}
